/**
 * AuthScreen - shared shell for every unauthenticated (or blocked) screen:
 * `/login` and the forced password change. Brand on the left, form on the
 * right, hairline between them; one column below 768px.
 *
 * The screen is always dark, regardless of the user's theme: it renders before
 * we know who they are, and it is the brand's first impression. That is
 * enforced by the `data-auth-surface` attribute set here, whose token block in
 * `index.css` uses literal values only — no theme token (`--background`,
 * `--card`, `--foreground`, …) and no `.dark` variant is read anywhere in the
 * auth CSS, so the theme toggle cannot reach these screens.
 */
import { cn } from "../../lib/utils";
import {
  AUTH_REVEAL_BRAND,
  AUTH_REVEAL_FOOTER,
  AUTH_REVEAL_HEADING,
  authRevealClass,
} from "./authReveal";

/** Kept here rather than in `branding.ts`: it is auth-screen copy, not brand metadata. */
const AUTH_TAGLINE = "Coding agents, one workspace";

export type AuthScreenProps = {
  /** Heading of the form column, e.g. "Sign in". */
  title: string;
  /** Optional line under the heading. */
  subtitle?: React.ReactNode;
  /** Form contents: fields, error, submit button. */
  children: React.ReactNode;
  /**
   * Rendered under the form, outside the `<form>` element — link rows,
   * "Sign out instead", and similar escape hatches.
   */
  footer?: React.ReactNode;
  /** Submit handler for the form wrapping `children`. */
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  /**
   * `"page"` (default) fills the viewport as a route. `"overlay"` covers the
   * running app, for screens that block further interaction.
   */
  layer?: "page" | "overlay";
  /** Marketing line in the brand column; hidden below 768px. */
  tagline?: string;
  /**
   * Identifies the step being shown on multi-step screens. Changing it remounts
   * the form column, so the heading/fields/submit replay their entrance while
   * the brand column stays put — without it, advancing a step swaps the copy
   * with no transition at all. Leave unset on single-step screens.
   */
  stepKey?: string;
};

export function AuthScreen(props: AuthScreenProps) {
  const layer = props.layer ?? "page";

  return (
    <div
      data-auth-surface=""
      className={cn(
        "auth-screen",
        layer === "overlay" ? "auth-screen--overlay" : "auth-screen--page",
      )}
    >
      <div className="auth-panels">
        <div className={cn("auth-brand", authRevealClass(AUTH_REVEAL_BRAND))}>
          <img
            src="/taskin-logo.png"
            alt="Taskin"
            className="auth-brand-mark"
            draggable={false}
            // Intrinsic size of the shipped PNG (IHDR: 1098x271); declaring it
            // keeps the brand column from reflowing once the image decodes.
            // CSS still drives the rendered width — this is only the ratio.
            width={1098}
            height={271}
          />
          <p className="auth-tagline">{props.tagline ?? AUTH_TAGLINE}</p>
        </div>

        <div className="auth-rule" aria-hidden="true" />

        <div className="auth-form-panel" key={props.stepKey}>
          <div className={authRevealClass(AUTH_REVEAL_HEADING)}>
            <h1 className="auth-title">{props.title}</h1>
            {props.subtitle === undefined ? null : (
              <p className="auth-subtitle">{props.subtitle}</p>
            )}
          </div>

          <form className="auth-form" onSubmit={props.onSubmit}>
            {props.children}
          </form>

          {props.footer === undefined ? null : (
            <div className={cn("auth-footer", authRevealClass(AUTH_REVEAL_FOOTER))}>
              {props.footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Submission failure. `role="alert"` announces it the moment it appears, which
 * is the only signal a screen-reader user gets that the submit did nothing.
 * Pass `id` and point the offending field's `aria-describedby` at it so the
 * reason is also read out when the user returns to the field.
 */
export function AuthError(props: { children: React.ReactNode; id?: string }) {
  return (
    <div className="auth-error" role="alert" id={props.id}>
      {props.children}
    </div>
  );
}
